import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface LessonReadRecord {
  openedAt: string;
  completedAt?: string;
  attempt: number;
  attemptStartedAt: string;
  attemptCompletedAt?: string;
}

interface LessonReadProgressState {
  byMicroTopic: Record<string, LessonReadRecord>;
  markOpened: (microTopicId: string, at?: string) => void;
  markRead: (microTopicId: string, at?: string) => void;
  reset: () => void;
}

function nowIso(at?: string): string {
  return at ?? new Date().toISOString();
}

function freshRecord(at: string): LessonReadRecord {
  return {
    openedAt: at,
    attempt: 1,
    attemptStartedAt: at,
  };
}

export function isLessonRead(
  record: LessonReadRecord | undefined,
  backendState?: 'taught' | 'untaught',
): boolean {
  return backendState === 'taught' || Boolean(record?.completedAt);
}

export function isLessonOpened(record: LessonReadRecord | undefined): boolean {
  return Boolean(record?.openedAt || record?.attemptStartedAt);
}

export const useLessonReadProgress = create<LessonReadProgressState>()(
  persist(
    (set) => ({
      byMicroTopic: {},

      markOpened: (microTopicId, at) => {
        if (!microTopicId) return;
        const stamp = nowIso(at);
        set((s) => {
          const prev = s.byMicroTopic[microTopicId];
          if (prev?.openedAt) return s;
          return {
            byMicroTopic: {
              ...s.byMicroTopic,
              [microTopicId]: prev
                ? { ...prev, openedAt: stamp, attemptStartedAt: prev.attemptStartedAt ?? stamp }
                : freshRecord(stamp),
            },
          };
        });
      },

      markRead: (microTopicId, at) => {
        if (!microTopicId) return;
        const stamp = nowIso(at);
        set((s) => {
          const prev = s.byMicroTopic[microTopicId] ?? freshRecord(stamp);
          return {
            byMicroTopic: {
              ...s.byMicroTopic,
              [microTopicId]: {
                ...prev,
                openedAt: prev.openedAt ?? stamp,
                completedAt: prev.completedAt ?? stamp,
                attemptCompletedAt: stamp,
              },
            },
          };
        });
      },



      reset: () => set({ byMicroTopic: {} }),
    }),
    {
      name: 'asc_lesson_read_progress_v1',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ byMicroTopic: state.byMicroTopic }),
    },
  ),
);
